import imageCompression from 'browser-image-compression'
import type { ValueOf } from 'type-fest'
import { getImageDownloadFileExt } from 'src/components/ArtworkDetail/utils'
import type { Media } from 'src/services/apollo/generated/graphql'
import { asyncScopedObjectUrl } from './hooks/useObjectUrl'
import type { ImageDimensions } from './imageUtil'
import {
  getCompressedDimensions,
  getImageUrlDimensionOrThrow,
  resizeImage,
} from './imageUtil'
import { downloadMedia, getPublicUrl } from './media'
import { uploadMedia } from './upload'

type OneOnly<Obj, Key extends keyof Obj> = {
  [key in Exclude<keyof Obj, Key>]+?: undefined
} & Pick<Obj, Key>
type OneOfByKey<Obj> = { [key in keyof Obj]: OneOnly<Obj, key> }
type OneOf<Obj> = ValueOf<OneOfByKey<Obj>>

type UnObj = OneOf<{
  file: File
  media: Media
}>

type IfHas = {
  file: File
  media: Media
  url: string
}

export type UnFileCompressionOptions = {
  /** @default 1024 */
  maxWidthOrHeight?: number
  /** @default 1 */
  maxSizeMB?: number
  divisibleBy?: number
  /**
   * Ignore `maxWidthOrHeight` and `maxSizeMB` when media already exists.
   * @default true
   */
  preferExistingMedia?: boolean
  onCompressed?: (file: UnFile) => Promise<void> | void
}

export type UnFileToMediaOptions = Omit<
  UnFileCompressionOptions,
  'onCompressed'
> & {
  /**
   * Note: whenever possible, you should handle `onCompressed` and replace
   * your file with the compressed one to avoid unnecessary recompression.
   */
  onCompressed:
    | NonNullable<UnFileCompressionOptions['onCompressed']>
    | undefined

  uploadMedia?: Parameters<typeof uploadMedia>[1]
}

// universal file
export class UnFile {
  private file?: File
  private media?: Media

  private url?: string
  private rc = 0

  private constructor(
    private obj: UnObj,
    // mutable: toFile() may correct the extension if imageType was absent on the media
    public name: string,
  ) {
    this.file = obj.file
    this.media = obj.media

    if (import.meta.env.DEV) {
      Object.defineProperty(this, 'antiClone', {
        enumerable: true,
        get() {
          console.error(
            'UnFile should not be cloned',
            new Error('Stack:').stack,
          )
          throw new Error('UnFile should not be cloned')
        },
      })
    }
  }

  static fromMedia(
    media: Media,
    name = `pixai-media_${media.id}.${getImageDownloadFileExt({ media })}`,
  ) {
    return new UnFile({ media }, name)
  }

  static fromFile(file: File, name = file.name) {
    return new UnFile({ file }, name)
  }

  static async fromCanvas(
    canvas: HTMLCanvasElement,
    name = 'canvas.png',
    type = 'image/png',
  ) {
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, type),
    )
    if (!blob) throw new Error('Canvas to blob failed')
    return UnFile.fromFile(new File([blob], name, { type }))
  }

  static async fromOffscreenCanvas(
    offscreenCanvas: OffscreenCanvas,
    name = 'canvas.png',
    type = 'image/png',
  ) {
    const blob = await offscreenCanvas.convertToBlob({ type })
    if (!blob) throw new Error('Offscreen canvas to blob failed')
    return UnFile.fromFile(new File([blob], name, { type }))
  }

  ifIs<T, K extends keyof UnObj>(
    type: K,
    fn: (obj: NonNullable<UnObj[K]>) => T,
  ): T | undefined {
    if (this.obj[type]) return fn(this.obj[type] as any)
  }

  ifHas<T, K extends keyof IfHas>(
    type: K,
    fn: (obj: IfHas[K]) => T,
  ): T | undefined {
    if (this[type as never]) return fn(this[type] as any)
  }

  createUrl(): string {
    if (this.url) {
      if (this.rc) this.rc++
      return this.url
    }

    if (this.file) {
      this.rc++
      return (this.url = URL.createObjectURL(this.file))
    }

    if (this.media) {
      this.url = getPublicUrl(this.media)
      if (!this.url) throw new Error('Media has no public URL')
      this.rc++
      return this.url
    }

    throw new Error('No file or media, this should be unreachable')
  }

  revokeUrl() {
    if (import.meta.env.DEV && this.rc <= 0)
      console.error('Revoking URL of a UnFile that has not been created')
    if (!this.url || this.rc <= 0) return

    this.rc--
    if (this.rc === 0) {
      URL.revokeObjectURL(this.url)
      this.url = undefined
    }
  }

  async toCompressed(opts: UnFileCompressionOptions): Promise<UnFile> {
    const {
      maxWidthOrHeight = 2048,
      maxSizeMB = 4,
      divisibleBy,
      preferExistingMedia = true,
      onCompressed,
    } = opts

    const dim = await this.getDimensions()

    const fitCompressionOptions = async () => {
      if (divisibleBy)
        if (dim.width % divisibleBy || dim.height % divisibleBy) return false

      if (preferExistingMedia && this.media) return true

      if (dim.width > maxWidthOrHeight || dim.height > maxWidthOrHeight)
        return false

      if ((await this.toFile()).size > maxSizeMB * 1024 * 1024) return false

      return true
    }

    if (await fitCompressionOptions()) return this

    let file = await this.toFile()

    if (divisibleBy) {
      const { width, height } = getCompressedDimensions({
        ...dim,
        divisibleBy,
        maxEdge: maxWidthOrHeight,
      })
      if (!width || !height) throw new Error('Invalid compressed dimensions')
      file = await resizeImage(file, width, height)
    }

    let compressedFile
    try {
      compressedFile = await imageCompression(file, {
        maxWidthOrHeight,
        maxSizeMB,
        useWebWorker: true,
        fileType: 'image/webp',
      })
    } catch {
      try {
        compressedFile = await imageCompression(file, {
          maxWidthOrHeight,
          maxSizeMB,
          useWebWorker: true,
          fileType: 'image/png',
        })
      } catch {
        compressedFile = await imageCompression(file, {
          maxWidthOrHeight,
          maxSizeMB,
          fileType: 'image/png',
        })
      }
    }

    const compressed = UnFile.fromFile(compressedFile)
    await onCompressed?.(compressed)

    return compressed
  }

  async toFile() {
    if (this.file) return this.file
    if (this.media) {
      this.file = await downloadMedia(this.obj.media)
      if (!this.file) throw new Error('Download media failed')
      // imageType is not always populated on media; fall back to Content-Type from the response
      if (!this.media.imageType && this.file.type) {
        const ext = this.file.type.split('/')[1]
        if (ext) this.name = this.name.replace(/\.[^.]+$/, `.${ext}`)
      }
      return this.file
    }
    throw new Error('No file or media, this should be unreachable')
  }

  protected async _toMedia(opts?: Parameters<typeof uploadMedia>[1]) {
    if (this.media) return this.media
    if (this.file)
      return (this.media = (await uploadMedia(this.file, opts)).media)
    throw new Error('No file or media, this should be unreachable')
  }

  async toMedia({ uploadMedia, ...compressionOptions }: UnFileToMediaOptions) {
    const compressed = await this.toCompressed(compressionOptions)
    return compressed._toMedia(uploadMedia)
  }

  private dimCache: Promise<ImageDimensions> | undefined
  getDimensions() {
    if (!this.dimCache) this.dimCache = this._getDimensions()
    return this.dimCache
  }

  protected async _getDimensions() {
    if (this.media) {
      const { width, height } = this.media
      if (width && height) return { width, height }
    }
    return asyncScopedObjectUrl(this, getImageUrlDimensionOrThrow)
  }
}
